import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useHookstate } from '@hookstate/core';
import { onConnect } from '../socket/chat/connect';
import { onDisconnect } from '../socket/chat/disconnect';
import { onEnter } from '../socket/chat/enter';
import { onEvent } from '../socket/chat/event';
import { onInfo } from '../socket/chat/info';
import { onJoin } from '../socket/chat/join';
import { onLeave } from '../socket/chat/leave';
import { onMute } from '../socket/chat/mute';
import { ChatPayload, createChatPayload } from '../socket/chat/payload';
import { onDelete } from '../socket/chat/delete';
import { onPost } from '../socket/chat/post';
import { onUnmute } from '../socket/chat/unmute';
import { ChatSocket, useSocket } from './use-socket';
import { screenConfig as screenConfigState } from '../stores/backend/';
import { guardSocket } from '../lib/guard-socket';
import { isSuspended } from '../lib/suspension';
import { BackendPayload } from '../socket/backend/payloads';
import { useBackendPayload } from './use-backend';

export type Subscriber = (
    socket: ChatSocket,
    payload: ChatPayload,
    getBackendPayload: () => BackendPayload | undefined
) => () => void;

export const ChatContext = createContext({} as { chatPayload: ChatPayload });

export const useChat = () => {
    const { createChat } = useSocket();
    const [payload, setPayload] = useState<ChatPayload>();
    const { screenConfig } = useHookstate(screenConfigState);
    const { backendPayload } = useBackendPayload();
    const backendPayloadRef = useRef(backendPayload);
    backendPayloadRef.current = backendPayload;

    useEffect(() => {
        if (isSuspended() || !screenConfig.site.siteId.value) {
            return;
        }

        const transport = guardSocket(createChat());
        const chat = transport.socket;
        const payload = createChatPayload(chat);

        setPayload(payload);

        const subscriptions = [
            onConnect,
            onDelete,
            onDisconnect,
            onEnter,
            onInfo,
            onJoin,
            onLeave,
            onMute,
            onPost,
            onUnmute,
            onEvent,
        ].map((onFn: Subscriber) =>
            onFn(chat, payload, () => backendPayloadRef.current)
        );

        return () => {
            for (const unsubscribe of subscriptions) {
                unsubscribe();
            }
            transport.dispose();
        };
    }, [createChat, screenConfig.site.siteId.value]);

    return payload;
};

export const useChatPayload = () => useContext(ChatContext);
